import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GoogleMap, Marker } from '@react-google-maps/api';
import Sidebar from '../components/Sidebar';
import RichTextEditor from '../components/RichTextEditor';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Save, Download, Link as LinkIcon, Sparkles,
  Clock, MapPin, Camera, FileText, Image, GripVertical,
  Users, Navigation, Upload, Trash2, ChevronLeft, ChevronRight, ChevronDown,
  Menu, X, ExternalLink, Edit, FileCheck, Globe, Share2, Activity
} from 'lucide-react';
import { getTimezoneOffset, getCurrentTimeInTimezone } from '../utils/timezones';
import PlanShareDialog from '../components/PlanShareDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

export default function PlanBuilderEnhanced() {
  const { eventId, planId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [event, setEvent] = useState(null);
  const [courseTimings, setCourseTimings] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [photographerSections, setPhotographerSections] = useState([]);
  const [draggedSection, setDraggedSection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveTimeout, setSaveTimeout] = useState(null);
  const [showScrapeDialog, setShowScrapeDialog] = useState(false);
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  
  // Edit spot dialog states
  const [editSpotDialog, setEditSpotDialog] = useState(false);
  const [editingSpot, setEditingSpot] = useState(null);
  const [spotForm, setSpotForm] = useState({});
  const [availablePhotographers, setAvailablePhotographers] = useState([]);
  
  // Plan name editing
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedPlanName, setEditedPlanName] = useState('');
  
  // Auto-save timer
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  
  // Plan description editing with templates
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [planDescription, setPlanDescription] = useState('');
  const [templates, setTemplates] = useState([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(true);
  
  // Image library states
  const [showLibrarySelector, setShowLibrarySelector] = useState(false);
  const [libraryImages, setLibraryImages] = useState([]);
  const [libraryPacks, setLibraryPacks] = useState([]);
  const [selectedLibraryImages, setSelectedLibraryImages] = useState([]);
  const [librarySearchTerm, setLibrarySearchTerm] = useState('');
  const [selectedLibraryPack, setSelectedLibraryPack] = useState('All');

  // Public plan share dialog
  const [showShareDialog, setShowShareDialog] = useState(false);

  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  // Common gear suggestions by sport
  const commonGearBySport = {
    'Run': ['16-35mm f/2.8', '24-70mm f/2.8', '70-200mm f/2.8', 'Wide angle 16-35mm', 'Telephoto 300mm+', 'Flash/Strobe'],
    'Swimming': ['16-35mm f/2.8', '24-70mm f/2.8', '70-200mm f/2.8', 'Telephoto 300mm+', 'Underwater housing', 'Waterproof camera', 'Flash/Strobe'],
    'Bike': ['16-35mm f/2.8', '24-70mm f/2.8', '70-200mm f/2.8', 'Telephoto 300mm+', 'Drone', 'Remote camera'],
    'SKIERG': ['16-35mm f/2.8', '24-70mm f/2.8', '70-200mm f/2.8', 'Flash/Strobe', 'Wide angle 16-35mm'],
    'SLED PUSH': ['16-35mm f/2.8', '24-70mm f/2.8', '70-200mm f/2.8', 'Flash/Strobe', 'Wide angle 16-35mm'],
    'SLED PULL': ['16-35mm f/2.8', '24-70mm f/2.8', '70-200mm f/2.8', 'Flash/Strobe', 'Wide angle 16-35mm'],
    'BURPEE BROAD JUMPS': ['16-35mm f/2.8', '24-70mm f/2.8', '70-200mm f/2.8', 'Wide angle 16-35mm', 'Flash/Strobe'],
    'ROWING': ['16-35mm f/2.8', '24-70mm f/2.8', '70-200mm f/2.8', 'Flash/Strobe', 'Telephoto 300mm+'],
    'FARMERS CARRY': ['16-35mm f/2.8', '24-70mm f/2.8', '70-200mm f/2.8', 'Flash/Strobe'],
    'LUNGES': ['16-35mm f/2.8', '24-70mm f/2.8', 'Wide angle 16-35mm', 'Flash/Strobe'],
    'WALL BALLS': ['16-35mm f/2.8', '24-70mm f/2.8', '70-200mm f/2.8', 'Flash/Strobe', 'Wide angle 16-35mm'],
    'General': ['16-35mm f/2.8', '24-70mm f/2.8', '70-200mm f/2.8', 'Wide angle 16-35mm', 'Telephoto 300mm+', 'Flash/Strobe', 'Drone']
  };

  const getSuggestedGear = (modalities) => {
    const allSuggestions = new Set();
    (modalities || []).forEach(modality => {
      const gearList = commonGearBySport[modality] || commonGearBySport['General'];
      gearList.forEach(gear => allSuggestions.add(gear));
    });
    return Array.from(allSuggestions);
  };

  // Get timing suggestions based on selected modalities from actual course timing data
  const getTimingSuggestions = (modalities) => {
    const suggestions = [];
    if (courseTimings && courseTimings.modality_timings) {
      courseTimings.modality_timings.forEach(modalityTiming => {
        if ((modalities || []).includes(modalityTiming.modality)) {
          const timeParts = [];
          if (modalityTiming.start_time) timeParts.push(`Start: ${modalityTiming.start_time}`);
          if (modalityTiming.first_finisher_time) timeParts.push(`First Finisher: ${modalityTiming.first_finisher_time}`);
          if (modalityTiming.last_finisher_time) timeParts.push(`Last Finisher: ${modalityTiming.last_finisher_time}`);
          if (modalityTiming.cutoff_time) timeParts.push(`Cutoff: ${modalityTiming.cutoff_time}`);
          
          if (timeParts.length > 0) {
            suggestions.push({
              modality: modalityTiming.modality,
              timeInfo: timeParts.join(' | '),
              data: modalityTiming
            });
          }
        }
      });
    }
    return suggestions;
  };

  useEffect(() => {
    fetchData();
  }, [planId, eventId]);

  // Auto-save effect - save every 5 minutes
  useEffect(() => {
    if (!plan) return;

    // Clear any existing timer
    if (autoSaveTimer) {
      clearInterval(autoSaveTimer);
    }

    // Set up new auto-save timer (5 minutes = 300000ms)
    const timer = setInterval(() => {
      savePlan(true); // true indicates auto-save
    }, 300000);

    setAutoSaveTimer(timer);

    // Cleanup on unmount
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [plan?.id]); // Only re-run if plan ID changes

  const cleanOrphanedAssignments = async (planData) => {
    if (!planData.spot_assignments || planData.spot_assignments.length === 0) {
      return planData;
    }

    try {
      // Fetch current spots and photographers for this event
      const [spotsRes, photographersRes] = await Promise.all([
        axios.get(`${API}/spots?event_id=${eventId}`, config),
        axios.get(`${API}/events/${eventId}/photographers`, config)
      ]);

      const validSpotIds = new Set(spotsRes.data.map(s => s.id));
      const validPhotographerIds = new Set(photographersRes.data.map(p => p.id));

      // Filter out assignments with non-existent spots or photographers.
      // BUT: coverage virtual rows (is_coverage=true OR spot_id contains
      // "::cov::") have synthetic spot_ids that won't be in validSpotIds —
      // resolve them to original_spot_id for the existence check.
      const validAssignments = planData.spot_assignments.filter(assignment => {
        const isCoverage = assignment.is_coverage || (assignment.spot_id || '').includes('::cov::');
        const realSpotId = isCoverage
          ? (assignment.original_spot_id || (assignment.spot_id || '').split('::cov::')[0])
          : assignment.spot_id;
        const spotExists = validSpotIds.has(realSpotId);
        const photographerExists = validPhotographerIds.has(assignment.photographer_id);

        if (!spotExists) {
          console.warn(`Removing orphaned spot assignment: Spot "${assignment.spot_name}" (${assignment.spot_id}) no longer exists`);
        }
        if (!photographerExists) {
          console.warn(`Removing orphaned photographer assignment: "${assignment.photographer_name}" (${assignment.photographer_id}) no longer exists`);
        }

        return spotExists && photographerExists;
      });

      const removedCount = planData.spot_assignments.length - validAssignments.length;

      if (removedCount > 0) {
        console.log(`🧹 Cleaned ${removedCount} orphaned assignment(s) from plan`);

        // Update the plan in the backend to persist the cleanup — but ONLY
        // strip native (non-coverage) rows. Coverage rows are recomputed on
        // every GET /plans/{id} from current break_coverage data, so we must
        // not persist them as if they were canonical state.
        const persistable = validAssignments.filter((a) => !a.is_coverage && !(a.spot_id || '').includes('::cov::'));
        await axios.put(`${API}/plans/${planData.id}`, {
          spot_assignments: persistable
        }, config);

        toast.info(`Cleaned ${removedCount} outdated assignment(s)`, { duration: 3000 });
      }

      return { ...planData, spot_assignments: validAssignments };
    } catch (error) {
      console.error('Error cleaning orphaned assignments:', error);
      // Return original plan if cleanup fails
      return planData;
    }
  };

  const fetchData = async () => {
    try {
      const [eventRes, planRes, timingsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`, config),
        planId ? axios.get(`${API}/plans/${planId}`, config) : Promise.resolve(null),
        axios.get(`${API}/course-timing/event/${eventId}`, config).catch(() => ({ data: null })),
        axios.get(`${API}/content-templates`, config).catch(() => ({ data: [] }))
      ]);
      
      setEvent(eventRes.data);
      setCourseTimings(timingsRes.data);
      setTemplates(templatesRes.data);
      
      if (planRes) {
        // Clean up orphaned assignments before setting plan
        const cleanedPlan = await cleanOrphanedAssignments(planRes.data);
        setPlan(cleanedPlan);
        setPlanDescription(cleanedPlan.description_html || '');
        organizeSectionsByPhotographer(cleanedPlan);
      } else {
        // Create new plan - let backend auto-generate versioned name
        const newPlanRes = await axios.post(`${API}/plans`, {
          event_id: eventId,
          name: '' // Empty name will trigger auto-generation of "Event Name - v1", "v2", etc.
        }, config);
        setPlan(newPlanRes.data);
        setPlanDescription('');
        navigate(`/events/${eventId}/plan/${newPlanRes.data.id}`);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load plan data');
    } finally {
      setLoading(false);
    }
  };

  // Save plan function - used for both auto-save and manual save
  const savePlan = async (isAutoSave = false) => {
    if (!plan || !plan.id) {
      toast.error('No plan to save');
      return;
    }

    setSaving(true);
    
    // Show discrete toast for manual save
    if (!isAutoSave) {
      toast.info('Saving plan...', { duration: 1500 });
    }

    try {
      // Get current plan data from MongoDB to ensure we have the latest spot_assignments
      const currentPlanRes = await axios.get(`${API}/plans/${plan.id}`, config);
      const currentPlan = currentPlanRes.data;

      // Update plan with current state
      await axios.put(`${API}/plans/${plan.id}`, {
        name: plan.name,
        event_description: plan.event_description,
        description_html: planDescription,
        event_url: plan.event_url,
        event_date: plan.event_date,
        event_location: plan.event_location,
        weather_info: plan.weather_info,
        reference_videos: plan.reference_videos,
        reference_docs: plan.reference_docs,
        instructions: plan.instructions,
        course_description: plan.course_description,
        timing_overview: plan.timing_overview,
        spot_assignments: currentPlan.spot_assignments || plan.spot_assignments || []
      }, config);

      // Update local plan state with new updated_at time
      setPlan({ ...plan, updated_at: new Date().toISOString() });

      // Show success toast - discrete and brief
      if (isAutoSave) {
        toast.success('Plan auto-saved ✓', { duration: 2000 });
      } else {
        toast.success('Plan saved successfully ✓', { duration: 2000 });
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error('Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  // Save plan name
  const savePlanName = async (newName) => {
    if (!plan || !plan.id || !newName.trim()) {
      toast.error('Please enter a valid plan name');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/plans/${plan.id}`, {
        name: newName.trim()
      }, config);

      setPlan({ ...plan, name: newName.trim(), updated_at: new Date().toISOString() });
      setIsEditingName(false);
      toast.success('Plan name updated ✓', { duration: 2000 });
    } catch (error) {
      console.error('Error updating plan name:', error);
      toast.error('Failed to update plan name');
    } finally {
      setSaving(false);
    }
  };

  const organizeSectionsByPhotographer = (planData) => {
    if (!planData.spot_assignments || planData.spot_assignments.length === 0) {
      setPhotographerSections([]);
      return;
    }

    // Group spots by photographer
    const grouped = {};
    planData.spot_assignments.forEach(spot => {
      const photogId = spot.photographer_id || 'unassigned';
      const photogName = spot.photographer_name || 'Unassigned';
      
      if (!grouped[photogId]) {
        grouped[photogId] = {
          photographer_id: photogId,
          photographer_name: photogName,
          spots: []
        };
      }
      grouped[photogId].spots.push(spot);
    });

    // Sort spots within each photographer by arrive_time (chronological order)
    Object.values(grouped).forEach(section => {
      section.spots.sort((a, b) => {
        // Always sort by arrive_time first for chronological order
        if (a.arrive_time && b.arrive_time) {
          return a.arrive_time.localeCompare(b.arrive_time);
        }
        // If no arrive_time, fall back to order field
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        // Spots without time or order go to the end
        if (!a.arrive_time && b.arrive_time) return 1;
        if (a.arrive_time && !b.arrive_time) return -1;
        return 0;
      });
    });

    // Sort photographers by their earliest spot time
    const sortedSections = Object.values(grouped).sort((sectionA, sectionB) => {
      // Get earliest time for each photographer
      const getEarliestTime = (section) => {
        const times = section.spots
          .map(spot => spot.arrive_time)
          .filter(time => time && time.trim() !== '');
        
        if (times.length === 0) return '99:99'; // Put photographers without times at the end
        
        // Return the earliest time (smallest when sorted)
        return times.sort()[0];
      };

      const earliestA = getEarliestTime(sectionA);
      const earliestB = getEarliestTime(sectionB);

      // Debug logging
      console.log(`📊 Comparing: ${sectionA.photographer_name} (${earliestA}) vs ${sectionB.photographer_name} (${earliestB})`);

      return earliestA.localeCompare(earliestB);
    });

    // Log final sorted order
    console.log('📋 Final photographer order:');
    sortedSections.forEach((section, idx) => {
      const times = section.spots.map(s => s.arrive_time).filter(t => t);
      const earliest = times.length > 0 ? Math.min(...times.map(t => t)) : 'no time';
      console.log(`  ${idx + 1}. ${section.photographer_name} - Earliest: ${times.length > 0 ? times.sort()[0] : 'no time'} (${section.spots.length} spots)`);
    });

    setPhotographerSections(sortedSections);
  };

  const handleGenerateFromSpots = async () => {
    try {
      // Fetch spots for this event
      const spotsRes = await axios.get(`${API}/spots?event_id=${eventId}`, config);
      const photographersRes = await axios.get(`${API}/events/${eventId}/photographers`, config);

      const spots = spotsRes.data;
      const photographers = photographersRes.data;

      // Only include spots that have assigned photographers
      const assignedSpots = spots.filter(spot =>
        spot.assigned_photographers && spot.assigned_photographers.length > 0
      );

      // Coverage entries can apply even when the spot has no native
      // assigned_photographers, so we count those separately.
      const hasCoverage = spots.some(s => (s.break_coverage || []).length > 0);

      if (assignedSpots.length === 0 && !hasCoverage) {
        toast.info('No spots with assigned photographers found');
        return;
      }

      // Create spot assignments grouped by photographer
      const spotAssignments = [];

      assignedSpots.forEach(spot => {
        spot.assigned_photographers.forEach(photogId => {
          const photographer = photographers.find(p => p.id === photogId);

          // Use per-photographer times if available, fallback to spot defaults
          const pt = spot.photographer_times?.[photogId];
          const arriveTime = pt?.arrive_time || spot.arrive_time || '';
          const leaveTime = pt?.leave_time || spot.leave_time || '';

          spotAssignments.push({
            spot_id: spot.id,
            spot_name: spot.name,
            photographer_id: photogId,
            photographer_name: photographer?.name || 'Unknown',
            photographer_email: photographer?.email || '',
            required_gear: spot.required_gear || [],
            modalities: spot.modalities || [],
            arrive_time: arriveTime,
            leave_time: leaveTime,
            first_athlete_time: spot.first_athlete_time || '',
            description: spot.description || '',
            coverage_role: spot.coverage_role || '',
            priority: spot.priority || '',
            lat: spot.lat,
            lng: spot.lng,
            address: spot.address || '',
            reference_images: spot.reference_images || [],
            order: spotAssignments.length
          });
        });
      });

      // Append break_coverage entries as virtual spots — each becomes a row
      // in the covering photographer's section with the coverage time window.
      let coverageCount = 0;
      spots.forEach(spot => {
        (spot.break_coverage || []).forEach(c => {
          const coveringId = c.covering_photographer_id;
          if (!coveringId) return;
          const photographer = photographers.find(p => p.id === coveringId);
          const coversFor = c.covers_for_id ? photographers.find(p => p.id === c.covers_for_id) : null;
          spotAssignments.push({
            spot_id: `${spot.id}::cov::${c.id}`,
            spot_name: spot.name,
            photographer_id: coveringId,
            photographer_name: photographer?.name || 'Unknown',
            photographer_email: photographer?.email || '',
            required_gear: spot.required_gear || [],
            modalities: spot.modalities || [],
            arrive_time: c.start_time,
            leave_time: c.end_time,
            description: spot.description || '',
            coverage_role: spot.coverage_role || '',
            priority: spot.priority || '',
            lat: spot.lat,
            lng: spot.lng,
            address: spot.address || '',
            reference_images: spot.reference_images || [],
            notes: (c.note || '') + (coversFor ? `\n\n— covering for ${coversFor.name}` : '\n\n— break coverage'),
            is_coverage: true,
            coverage_id: c.id,
            original_spot_id: spot.id,
            coverage_for_photographer_id: c.covers_for_id,
            coverage_for_photographer_name: coversFor?.name || null,
            coverage_note: c.note,
            order: spotAssignments.length,
          });
          coverageCount += 1;
        });
      });

      // Update plan with new spot assignments
      await axios.put(`${API}/plans/${plan.id}`, {
        spot_assignments: spotAssignments
      }, config);

      const msg = coverageCount > 0
        ? `Generated plan with ${spotAssignments.length} assignments (${coverageCount} coverage)`
        : `Generated plan with ${spotAssignments.length} spot assignments!`;
      toast.success(msg);
      fetchData();
    } catch (error) {
      console.error('Error generating from spots:', error);
      toast.error('Failed to generate plan from spots');
    }
  };

  const handleSectionDragStart = (e, index) => {
    setDraggedSection(index);
  };

  const handleSectionDragOver = (e) => {
    e.preventDefault();
  };

  const handleSectionDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedSection === null || draggedSection === dropIndex) return;

    const newSections = [...photographerSections];
    const [removed] = newSections.splice(draggedSection, 1);
    newSections.splice(dropIndex, 0, removed);
    
    setPhotographerSections(newSections);

    // Flatten back to spot_assignments with updated order
    const newSpotAssignments = [];
    newSections.forEach(section => {
      section.spots.forEach(spot => {
        newSpotAssignments.push({
          ...spot,
          order: newSpotAssignments.length
        });
      });
    });

    try {
      await axios.put(`${API}/plans/${plan.id}`, { 
        spot_assignments: newSpotAssignments 
      }, config);
      setPlan({ ...plan, spot_assignments: newSpotAssignments });
      toast.success('Photographer order updated');
    } catch (error) {
      console.error('Error reordering:', error);
      toast.error('Failed to reorder photographers');
      fetchData(); // Revert on error
    }
    
    setDraggedSection(null);
  };

  const getStaticMapUrl = (lat, lng) => {
    // Generate SVG map as base64 data URI - using distinct colors so it's visible
    const svg = `<svg width="600" height="400" xmlns="http://www.w3.org/2000/svg">
      <rect width="600" height="400" fill="#e8f5e9"/>
      <g stroke="#c8e6c9" stroke-width="2">
        <line x1="0" y1="100" x2="600" y2="100"/>
        <line x1="0" y1="200" x2="600" y2="200"/>
        <line x1="0" y1="300" x2="600" y2="300"/>
        <line x1="150" y1="0" x2="150" y2="400"/>
        <line x1="300" y1="0" x2="300" y2="400"/>
        <line x1="450" y1="0" x2="450" y2="400"/>
      </g>
      <g stroke="#81c784" stroke-width="4">
        <line x1="0" y1="200" x2="600" y2="200"/>
        <line x1="300" y1="0" x2="300" y2="400"/>
      </g>
      <g transform="translate(300, 200)">
        <ellipse cx="0" cy="25" rx="8" ry="4" fill="black" opacity="0.3"/>
        <path d="M 0,-25 C -13,-25 -25,-13 -25,0 C -25,15 0,25 0,25 C 0,25 25,15 25,0 C 25,-13 13,-25 0,-25 Z" fill="#DC2626" stroke="white" stroke-width="2"/>
        <circle cx="0" cy="-10" r="6" fill="white"/>
      </g>
      <rect x="10" y="360" width="220" height="30" fill="white" opacity="0.95" rx="4"/>
      <text x="120" y="381" font-family="Arial, sans-serif" font-size="12" fill="#1b5e20" text-anchor="middle" font-weight="600">${lat.toFixed(6)}, ${lng.toFixed(6)}</text>
      <rect x="400" y="10" width="190" height="25" fill="white" opacity="0.95" rx="4"/>
      <text x="495" y="27" font-family="Arial, sans-serif" font-size="13" fill="#2e7d32" text-anchor="middle" font-weight="600">Map Location</text>
    </svg>`;
    
    // Convert to base64 data URI
    const base64Svg = btoa(unescape(encodeURIComponent(svg)));
    const dataUri = `data:image/svg+xml;base64,${base64Svg}`;
    console.log('🗺️ Map generated - green background with street grid');
    return dataUri;
  };

  const getDirectionsUrl = (lat, lng) => {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  };

  const handleExportPDF = async () => {
    setGeneratingPDF(true);
    try {
      // Import html2canvas
      const html2canvas = (await import('html2canvas')).default;
      
      toast.info('Capturing map screenshots...');
      
      // Capture all map screenshots
      const mapScreenshots = {};
      const mapElements = document.querySelectorAll('[data-spot-map]');
      
      for (const mapElement of mapElements) {
        const spotId = mapElement.getAttribute('data-spot-id');
        if (spotId) {
          try {
            // Capture the map element
            const canvas = await html2canvas(mapElement, {
              useCORS: true,
              allowTaint: true,
              backgroundColor: null,
              scale: 2, // Higher quality
            });
            
            // Convert to base64
            const imageData = canvas.toDataURL('image/png');
            mapScreenshots[spotId] = imageData;
            console.log(`✅ Captured map for spot: ${spotId}`);
          } catch (error) {
            console.error(`Failed to capture map for spot ${spotId}:`, error);
          }
        }
      }
      
      console.log(`📸 Captured ${Object.keys(mapScreenshots).length} map screenshots`);
      toast.info('Generating PDF...');
      
      // Send to backend with map screenshots
      const response = await axios.post(
        `${API}/plans/${plan.id}/export-pdf`,
        { mapScreenshots },
        { 
          ...config,
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${plan.name || 'plan'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleUpdateField = async (field, value) => {
    // Update local state immediately
    setPlan({ ...plan, [field]: value });
    
    // Clear existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    
    // Set new timeout for auto-save (500ms debounce)
    const timeout = setTimeout(async () => {
      setSaving(true);
      try {
        await axios.put(`${API}/plans/${plan.id}`, { [field]: value }, config);
      } catch (error) {
        toast.error('Failed to save changes');
      } finally {
        setSaving(false);
      }
    }, 500);
    
    setSaveTimeout(timeout);
  };

  const handleSaveDescription = async () => {
    if (!plan || !plan.id) {
      toast.error('No plan available to save');
      return;
    }

    // Check for base64 images (which make payload too large)
    if (planDescription && planDescription.includes('data:image')) {
      toast.error('Please upload images using the upload button instead of pasting them directly. Pasted images make the description too large to save.');
      return;
    }

    try {
      console.log('Saving plan description...', planDescription?.length, 'characters');
      
      const response = await axios.put(`${API}/plans/${plan.id}`, {
        description_html: planDescription
      }, config);
      
      console.log('Plan description saved successfully:', response.data);
      
      // Update local plan state with the saved data
      setPlan({ ...plan, description_html: planDescription, updated_at: response.data.updated_at });
      setIsEditingDescription(false);
      toast.success('Description saved ✓', { duration: 2000 });
    } catch (error) {
      console.error('Error saving description:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save description');
    }
  };

  const handleInsertTemplate = (template) => {
    setPlanDescription(template.content_html);
    setShowTemplateDialog(false);
    toast.success('Template inserted', { duration: 2000 });
  };

  const handleImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/events/upload-description-image`, formData, {
        headers: {
          ...config.headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Return relative URL so it works in any environment (preview, production, custom domain)
      // Backend returns: /api/uploads/event-descriptions/xxx.png
      // We return: /api/uploads/event-descriptions/xxx.png (relative)
      return response.data.url;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
      return null;
    }
  };

  const handleEditSpot = async (spotAssignment) => {
    try {
      // Fetch the full spot data and photographers
      const [spotResponse, photographersResponse] = await Promise.all([
        axios.get(`${API}/spots/${spotAssignment.spot_id}`, config),
        axios.get(`${API}/events/${eventId}/photographers`, config)
      ]);
      
      const fullSpot = spotResponse.data;
      setAvailablePhotographers(photographersResponse.data);
      
      setEditingSpot(fullSpot);
      setSpotForm({
        name: fullSpot.name || '',
        description: fullSpot.description || '',
        coverage_role: fullSpot.coverage_role || 'Wide',
        modalities: fullSpot.modalities || [],
        required_gear: fullSpot.required_gear || [],
        arrive_time: fullSpot.arrive_time || '',
        leave_time: fullSpot.leave_time || '',
        first_athlete_time: fullSpot.first_athlete_time || '',
        break_time_1: fullSpot.break_time_1 || '',
        break_time_2: fullSpot.break_time_2 || '',
        use_led_light: fullSpot.use_led_light || false,
        address: fullSpot.address || '',
        lat: fullSpot.lat,
        lng: fullSpot.lng,
        reference_images: fullSpot.reference_images || [],
        assigned_photographers: fullSpot.assigned_photographers || [],
        photographer_times: fullSpot.photographer_times || {},
        priority: fullSpot.priority || 'Medium'
      });
      setEditSpotDialog(true);
    } catch (error) {
      console.error('Error fetching spot:', error);
      toast.error('Failed to load spot details');
    }
  };

  const handleSaveSpot = async () => {
    try {
      // Save the spot changes
      await axios.put(`${API}/spots/${editingSpot.id}`, spotForm, config);
      
      // Fetch the updated spot data
      const updatedSpotRes = await axios.get(`${API}/spots/${editingSpot.id}`, config);
      const updatedSpot = updatedSpotRes.data;
      
      // Update the spot in the plan's spot_assignments
      // Use per-photographer times when available
      const photographerTimes = updatedSpot.photographer_times || {};
      const updatedAssignments = plan.spot_assignments.map(assignment => {
        if (assignment.spot_id === updatedSpot.id) {
          const pt = photographerTimes[assignment.photographer_id];
          return {
            ...assignment,
            spot_name: updatedSpot.name,
            description: updatedSpot.description,
            address: updatedSpot.address,
            lat: updatedSpot.lat,
            lng: updatedSpot.lng,
            modalities: updatedSpot.modalities,
            required_gear: updatedSpot.required_gear,
            arrive_time: pt?.arrive_time || updatedSpot.arrive_time,
            leave_time: pt?.leave_time || updatedSpot.leave_time,
            first_athlete_time: updatedSpot.first_athlete_time,
            last_athlete_time: updatedSpot.last_athlete_time,
            cutoff_time: updatedSpot.cutoff_time,
            flash_required: updatedSpot.flash_required,
            coverage_role: updatedSpot.coverage_role,
            reference_images: updatedSpot.reference_images
          };
        }
        return assignment;
      });
      
      // Save the updated assignments back to the plan
      await axios.put(`${API}/plans/${plan.id}`, {
        spot_assignments: updatedAssignments
      }, config);
      
      // Update local state
      const updatedPlan = { ...plan, spot_assignments: updatedAssignments };
      setPlan(updatedPlan);
      organizeSectionsByPhotographer(updatedPlan);
      
      toast.success('Spot updated successfully!');
      setEditSpotDialog(false);
    } catch (error) {
      console.error('Error saving spot:', error);
      toast.error('Failed to save spot');
    }
  };

  const handleAddPhotographer = (photographerId) => {
    const current = spotForm.assigned_photographers || [];
    if (!current.includes(photographerId)) {
      setSpotForm({
        ...spotForm,
        assigned_photographers: [...current, photographerId]
      });
    }
  };

  const handleRemovePhotographer = (photographerId) => {
    setSpotForm({
      ...spotForm,
      assigned_photographers: (spotForm.assigned_photographers || []).filter(id => id !== photographerId)
    });
  };

  const fetchLibraryImages = async () => {
    try {
      console.log('📚 Fetching image library...');
      const [imagesRes, packsRes] = await Promise.all([
        axios.get(`${API}/image-library`, config),
        axios.get(`${API}/image-packs`, config)  // Corrected endpoint
      ]);
      console.log('📚 Images received:', imagesRes.data?.length || 0);
      console.log('📦 Packs received:', packsRes.data?.length || 0);
      setLibraryImages(imagesRes.data || []);
      setLibraryPacks(packsRes.data || []);
      
      if (!imagesRes.data || imagesRes.data.length === 0) {
        console.warn('⚠️ No images in library');
        toast.info('No images in library yet. Please upload images to the Image Library first.');
      }
    } catch (error) {
      console.error('❌ Error fetching library:', error);
      toast.error('Failed to load image library');
      setLibraryImages([]);
      setLibraryPacks([]);
    }
  };

  const toggleLibraryImageSelection = (imageId) => {
    setSelectedLibraryImages(prev => 
      prev.includes(imageId) ? prev.filter(id => id !== imageId) : [...prev, imageId]
    );
  };

  const handleAddLibraryImages = async () => {
    if (selectedLibraryImages.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    try {
      const selectedUrls = libraryImages
        .filter(img => selectedLibraryImages.includes(img.id))
        .map(img => img.image_url);

      const updatedImages = [...(spotForm.reference_images || []), ...selectedUrls];
      setSpotForm({...spotForm, reference_images: updatedImages});
      
      setSelectedLibraryImages([]);
      setShowLibrarySelector(false);
      toast.success(`✅ Added ${selectedLibraryImages.length} images from library`);
    } catch (error) {
      console.error('Error adding library images:', error);
      toast.error('Failed to add images');
    }
  };

  // Fetch library images when dialog opens
  React.useEffect(() => {
    if (showLibrarySelector) {
      fetchLibraryImages();
      setSelectedLibraryImages([]);
    }
  }, [showLibrarySelector]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Sidebar />
        <div className="main-content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />

      {/* Main Content */}
      <div className="main-content overflow-x-hidden">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <button
              type="button"
              onClick={() => navigate(`/events/${eventId}?tab=plans`)}
              className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-emerald-600 mb-3"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to plans
            </button>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex-1">
                {/* Editable Plan Name */}
                {isEditingName ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editedPlanName}
                      onChange={(e) => setEditedPlanName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          savePlanName(editedPlanName);
                        } else if (e.key === 'Escape') {
                          setIsEditingName(false);
                          setEditedPlanName('');
                        }
                      }}
                      className="text-3xl font-bold"
                      autoFocus
                    />
                    <Button
                      size="sm"
                      onClick={() => savePlanName(editedPlanName)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Save className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setIsEditingName(false);
                        setEditedPlanName('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <h1
                    className="text-3xl font-bold text-gray-900 cursor-pointer hover:text-emerald-600 transition-colors"
                    onClick={() => {
                      setIsEditingName(true);
                      setEditedPlanName(plan?.name || '');
                    }}
                    title="Click to edit plan name"
                  >
                    {plan?.name}
                  </h1>
                )}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-1">
                  <p className="text-gray-600">
                    {event?.name}
                  </p>
                  {event?.timezone && (
                    <div className="flex items-center gap-1 text-gray-600">
                      <Globe className="w-3 h-3" />
                      <span className="text-sm">
                        {event.timezone} ({getTimezoneOffset(event.timezone)})
                        <span className="text-gray-500 ml-2">
                          Current: {getCurrentTimeInTimezone(event.timezone)}
                        </span>
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    {saving && <span className="text-sm text-emerald-600">● Saving...</span>}
                    {!saving && plan?.updated_at && (
                      <span className="text-sm text-gray-400">
                        Last saved: {new Date(plan.updated_at).toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={() => savePlan(false)}
                  disabled={saving}
                  variant="outline"
                  className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Plan'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleGenerateFromSpots}
                  className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Generate from Spots
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowShareDialog(true)}
                  className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                  data-testid="open-plan-share-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Online share
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/events/${eventId}/plan/${planId}/live`)}
                  className="border-blue-500 text-blue-700 hover:bg-blue-50"
                  data-testid="open-live-track-btn"
                >
                  <Activity className="w-4 h-4 mr-2" />
                  Live Track
                </Button>
                <Button
                  onClick={handleExportPDF}
                  disabled={generatingPDF}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {generatingPDF ? 'Generating...' : 'Export PDF'}
                </Button>
              </div>
            </div>
          </div>

          {/* Event Information with Editable Plan Description */}
          <Card className="p-6 mb-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  title={descriptionExpanded ? "Collapse" : "Expand"}
                >
                  {descriptionExpanded ? (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                  )}
                </button>
                <FileText className="w-5 h-5 text-emerald-600" />
                <h2 className="text-xl font-semibold">
                  Plan Description
                </h2>
              </div>
              <div className="flex gap-2">
                {isEditingDescription ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowTemplateDialog(true)}
                    >
                      <FileCheck className="w-4 h-4 mr-2" />
                      Insert Template
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setIsEditingDescription(false);
                        setPlanDescription(plan?.description_html || '');
                      }}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveDescription}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setIsEditingDescription(true)}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Description
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                    >
                      {descriptionExpanded ? 'Collapse' : 'Expand'}
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            {descriptionExpanded && (
              <div className="space-y-4 transition-all duration-300">
                <div>
                  <Label className="text-sm text-gray-500">Event Name</Label>
                  <div className="text-base font-medium text-gray-900 p-2 bg-gray-50 rounded">
                    {event?.name || 'Loading...'}
                  </div>
                </div>
                
                <div>
                  <Label>Description (Editable at Plan Level)</Label>
                  {isEditingDescription ? (
                    <RichTextEditor
                      content={planDescription}
                      onChange={setPlanDescription}
                      onImageUpload={handleImageUpload}
                      placeholder="Add plan-specific description with rich formatting, images, videos, and more..."
                    />
                  ) : (
                    <div className="min-h-[100px]">
                      {planDescription ? (
                        <div 
                          className="prose prose-slate max-w-none p-4 bg-white rounded-lg border"
                          style={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                          dangerouslySetInnerHTML={{ __html: planDescription }}
                        />
                      ) : (
                        <div className="text-gray-400 italic p-4 bg-gray-50 rounded-lg border text-center">
                          No plan description yet. Click "Edit Description" to add one with rich formatting, images, and templates.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {!descriptionExpanded && (
              <div className="text-sm text-gray-500 italic">
                {planDescription ? 'Click to expand and view description...' : 'No description added yet'}
              </div>
            )}
          </Card>

          {/* Template Selection Dialog */}
          <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Insert Content Template</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {templates.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">
                    No templates available. Create one from the Content Templates page.
                  </p>
                ) : (
                  templates.map((template) => (
                    <Card
                      key={template.id}
                      className="p-4 cursor-pointer hover:border-emerald-500 transition-all"
                      onClick={() => handleInsertTemplate(template)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold mb-1">{template.name}</h3>
                          <div 
                            className="text-sm text-slate-600 line-clamp-2"
                            dangerouslySetInnerHTML={{ 
                              __html: template.content_html.replace(/<[^>]*>/g, ' ').substring(0, 150) + '...' 
                            }}
                          />
                        </div>
                        <Button size="sm" variant="outline">
                          Insert
                        </Button>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Photographer Sections */}
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Users className="w-6 h-6 text-emerald-600" />
                Photographer Assignments ({photographerSections.length})
              </h2>
            </div>

            {photographerSections.length === 0 ? (
              <Card className="p-12 text-center shadow-sm">
                <Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No Assignments Yet
                </h3>
                <p className="text-gray-500 mb-4">
                  Click "Generate from Spots" to create photographer assignments from your event spots
                </p>
                <Button 
                  onClick={handleGenerateFromSpots}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Generate from Spots
                </Button>
              </Card>
            ) : (
              <div className="space-y-8">
                {photographerSections.map((section, sectionIndex) => (
                  <PhotographerSection
                    key={section.photographer_id}
                    section={section}
                    sectionIndex={sectionIndex}
                    onDragStart={handleSectionDragStart}
                    onDragOver={handleSectionDragOver}
                    onDrop={handleSectionDrop}
                    isDragging={draggedSection === sectionIndex}
                    getStaticMapUrl={getStaticMapUrl}
                    getDirectionsUrl={getDirectionsUrl}
                    onEditSpot={handleEditSpot}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Spot Dialog */}
      <Dialog open={editSpotDialog} onOpenChange={setEditSpotDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Spot: {editingSpot?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Spot Name</Label>
                <Input
                  value={spotForm.name || ''}
                  onChange={(e) => setSpotForm({...spotForm, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Coverage Role</Label>
                <select
                  className="w-full border rounded-lg p-2"
                  value={spotForm.coverage_role || 'Wide'}
                  onChange={(e) => setSpotForm({...spotForm, coverage_role: e.target.value})}
                >
                  <option value="Wide">Wide</option>
                  <option value="Detail">Detail</option>
                  <option value="Portrait">Portrait</option>
                  <option value="Action">Action</option>
                </select>
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <textarea
                className="w-full border rounded-lg p-2 min-h-[80px]"
                value={spotForm.description || ''}
                onChange={(e) => setSpotForm({...spotForm, description: e.target.value})}
              />
            </div>

            {/* Scene Types / Modalities */}
            <div>
              <Label className="block mb-2">Scene Types / Activities (Select all that apply)</Label>
              <div className="flex flex-wrap gap-2">
                {/* Show existing modalities from spots + common ones */}
                {[...new Set([
                  ...(spotForm.modalities || []),
                  'Wide Shot', 'Detail', 'Portrait', 'Group', 'Action', 'Candid',
                  'Run', 'Swimming', 'Bike', 'Ceremony', 'Reception', 'Stage',
                  'Exterior', 'Interior', 'Headshots'
                ])].map(modality => {
                  const isChecked = (spotForm.modalities || []).includes(modality);
                  return (
                    <button
                      key={modality}
                      type="button"
                      onClick={() => {
                        const modalities = spotForm.modalities || [];
                        if (isChecked) {
                          setSpotForm({...spotForm, modalities: modalities.filter(m => m !== modality)});
                        } else {
                          setSpotForm({...spotForm, modalities: [...modalities, modality]});
                        }
                      }}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        isChecked ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {modality}
                    </button>
                  );
                })}
              </div>
              
              {/* Custom Activity Input */}
              <div className="mt-3">
                <Label className="text-sm">Custom Activity</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add custom station/activity"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        const modalities = spotForm.modalities || [];
                        if (!modalities.includes(e.target.value.trim())) {
                          setSpotForm({...spotForm, modalities: [...modalities, e.target.value.trim()]});
                          e.target.value = '';
                          toast.success('Custom activity added');
                        }
                      }
                    }}
                    className="text-sm"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={(e) => {
                      const input = e.target.parentElement.querySelector('input');
                      if (input.value.trim()) {
                        const modalities = spotForm.modalities || [];
                        if (!modalities.includes(input.value.trim())) {
                          setSpotForm({...spotForm, modalities: [...modalities, input.value.trim()]});
                          input.value = '';
                          toast.success('Custom activity added');
                        }
                      }
                    }}
                  >
                    + Add
                  </Button>
                </div>
              </div>
              
              {/* Selected Modalities Display */}
              {spotForm.modalities && spotForm.modalities.length > 0 && (
                <div className="mt-3">
                  <Label className="text-sm mb-2 block">Selected: </Label>
                  <div className="flex flex-wrap gap-2">
                    {spotForm.modalities.map((mod, idx) => (
                      <span key={idx} className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs flex items-center gap-2">
                        {mod}
                        <button
                          onClick={() => {
                            setSpotForm({
                              ...spotForm,
                              modalities: spotForm.modalities.filter(m => m !== mod)
                            });
                          }}
                          className="hover:text-red-200"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Priority and Max Photographers */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Priority</Label>
                <select
                  className="w-full border rounded-lg p-2"
                  value={spotForm.priority || 'Medium'}
                  onChange={(e) => setSpotForm({...spotForm, priority: e.target.value})}
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
              <div>
                <Label>Max Photographers</Label>
                <Input
                  type="number"
                  min="1"
                  value={spotForm.max_photographers || 1}
                  onChange={(e) => setSpotForm({...spotForm, max_photographers: parseInt(e.target.value)})}
                />
              </div>
            </div>

            {/* Required Gear with Suggestions */}
            <div className="border-t pt-4">
              <Label className="block mb-2">📷 Required Gear</Label>
              
              {/* Suggested Gear */}
              {spotForm.modalities && spotForm.modalities.length > 0 && (
                <div className="mb-3 p-3 bg-blue-50 rounded border border-blue-200">
                  <p className="text-xs font-medium text-blue-800 mb-2">✨ Suggested Gear for Selected Modalities:</p>
                  <div className="flex flex-wrap gap-2">
                    {getSuggestedGear(spotForm.modalities).map((gear, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          if (!(spotForm.required_gear || []).includes(gear)) {
                            setSpotForm({...spotForm, required_gear: [...(spotForm.required_gear || []), gear]});
                            toast.success(`Added ${gear}`);
                          }
                        }}
                        className="bg-white border border-blue-300 px-2 py-1 rounded text-xs hover:bg-blue-100 transition-colors"
                      >
                        + {gear}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Current Required Gear */}
              <div className="flex flex-wrap gap-2 mb-2">
                {(spotForm.required_gear || []).map((gear, idx) => (
                  <span key={idx} className="bg-emerald-100 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                    {gear}
                    <button
                      onClick={() => {
                        const newGear = [...(spotForm.required_gear || [])];
                        newGear.splice(idx, 1);
                        setSpotForm({...spotForm, required_gear: newGear});
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              
              {/* Add Custom Gear */}
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Add custom gear (e.g., 85mm f/1.4)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      const newGear = [...(spotForm.required_gear || []), e.target.value.trim()];
                      setSpotForm({...spotForm, required_gear: newGear});
                      e.target.value = '';
                    }
                  }}
                  className="flex-1"
                />
              </div>
            </div>

            {/* Timing */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Arrive Time</Label>
                <Input
                  type="time"
                  value={spotForm.arrive_time || ''}
                  onChange={(e) => setSpotForm({...spotForm, arrive_time: e.target.value})}
                />
              </div>
              <div>
                <Label>Leave Time</Label>
                <Input
                  type="time"
                  value={spotForm.leave_time || ''}
                  onChange={(e) => setSpotForm({...spotForm, leave_time: e.target.value})}
                />
              </div>
              <div>
                <Label>First Athlete Time</Label>
                <Input
                  type="time"
                  value={spotForm.first_athlete_time || ''}
                  onChange={(e) => setSpotForm({...spotForm, first_athlete_time: e.target.value})}
                />
              </div>
              <div>
                <Label>Last Athlete Time</Label>
                <Input
                  type="time"
                  value={spotForm.last_athlete_time || ''}
                  onChange={(e) => setSpotForm({...spotForm, last_athlete_time: e.target.value})}
                />
              </div>
              <div>
                <Label>Cutoff Time</Label>
                <Input
                  type="time"
                  value={spotForm.cutoff_time || ''}
                  onChange={(e) => setSpotForm({...spotForm, cutoff_time: e.target.value})}
                />
              </div>
            </div>

            {/* Timing Suggestions */}
            {spotForm.modalities && spotForm.modalities.length > 0 && getTimingSuggestions(spotForm.modalities).length > 0 && (
              <div className="border border-amber-200 rounded-lg p-3 bg-amber-50">
                <p className="text-sm font-semibold text-amber-800 mb-2">💡 Timing Suggestions from Course Data:</p>
                <div className="space-y-2">
                  {getTimingSuggestions(spotForm.modalities).map((suggestion, idx) => (
                    <div key={idx} className="bg-white p-2 rounded border text-sm">
                      <p className="font-medium text-gray-900 mb-1">{suggestion.modality}</p>
                      <p className="text-gray-600 mb-2 text-xs">{suggestion.timeInfo}</p>
                      <button
                        onClick={() => {
                          // Auto-populate times: arrive 1hr before start, leave at cutoff
                          const updates = {};
                          if (suggestion.data.start_time) {
                            // Calculate 1 hour before start time
                            const [hours, minutes] = suggestion.data.start_time.split(':');
                            const startMinutes = parseInt(hours) * 60 + parseInt(minutes);
                            const arriveMinutes = startMinutes - 60;
                            const arriveHours = Math.floor(arriveMinutes / 60);
                            const arriveMins = arriveMinutes % 60;
                            updates.arrive_time = `${String(arriveHours).padStart(2, '0')}:${String(arriveMins).padStart(2, '0')}`;
                          }
                          if (suggestion.data.cutoff_time) {
                            updates.leave_time = suggestion.data.cutoff_time;
                            updates.cutoff_time = suggestion.data.cutoff_time;
                          }
                          if (suggestion.data.first_finisher_time) {
                            updates.first_athlete_time = suggestion.data.first_finisher_time;
                          }
                          if (suggestion.data.last_finisher_time) {
                            updates.last_athlete_time = suggestion.data.last_finisher_time;
                          }
                          setSpotForm({...spotForm, ...updates});
                          toast.success(`Applied timing from ${suggestion.modality}`);
                        }}
                        className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded text-xs"
                      >
                        Apply This Timing
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Break Time 1</Label>
                <Input
                  placeholder="e.g., 10:00-10:15"
                  value={spotForm.break_time_1 || ''}
                  onChange={(e) => setSpotForm({...spotForm, break_time_1: e.target.value})}
                />
              </div>
              <div>
                <Label>Break Time 2</Label>
                <Input
                  placeholder="e.g., 13:00-13:15"
                  value={spotForm.break_time_2 || ''}
                  onChange={(e) => setSpotForm({...spotForm, break_time_2: e.target.value})}
                />
              </div>
            </div>

            {/* Per-photographer times */}
            {spotForm.assigned_photographers && spotForm.assigned_photographers.length > 0 && (
              <div className="border-t pt-4">
                <Label className="block text-sm font-medium mb-2">👥 Individual Photographer Times</Label>
                <p className="text-xs text-gray-500 mb-3">Each photographer can arrive and leave at different times. Leave blank to use the spot's default times.</p>
                <div className="space-y-3">
                  {spotForm.assigned_photographers.map(photogId => {
                    const photog = availablePhotographers?.find(p => p.id === photogId);
                    const pt = spotForm.photographer_times?.[photogId] || {};
                    return (
                      <div key={photogId} className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <p className="text-sm font-semibold text-purple-900 mb-2">{photog?.name || photogId}</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Arrive</Label>
                            <Input
                              type="time"
                              value={pt.arrive_time || ''}
                              placeholder={spotForm.arrive_time || ''}
                              onChange={(e) => {
                                const newTimes = { ...(spotForm.photographer_times || {}) };
                                newTimes[photogId] = { ...(newTimes[photogId] || {}), arrive_time: e.target.value || null };
                                setSpotForm({ ...spotForm, photographer_times: newTimes });
                              }}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Leave</Label>
                            <Input
                              type="time"
                              value={pt.leave_time || ''}
                              placeholder={spotForm.leave_time || ''}
                              onChange={(e) => {
                                const newTimes = { ...(spotForm.photographer_times || {}) };
                                newTimes[photogId] = {
                                  arrive_time: newTimes[photogId]?.arrive_time || null,
                                  leave_time: e.target.value || null
                                };
                                setSpotForm({ ...spotForm, photographer_times: newTimes });
                              }}
                              className="text-sm"
                            />
                          </div>
                        </div>
                        {!pt.arrive_time && !pt.leave_time && (
                          <p className="text-[10px] text-gray-400 mt-1">Using spot default: {spotForm.arrive_time || '??'} - {spotForm.leave_time || '??'}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lighting Options */}
            <div className="border-t pt-4">
              <Label className="block text-sm font-medium mb-2">💡 Lighting Options</Label>
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={spotForm.has_flash || false}
                    onChange={(e) => setSpotForm({...spotForm, has_flash: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Flash Required</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={spotForm.use_led_flash || false}
                    onChange={(e) => setSpotForm({...spotForm, use_led_flash: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Use LED/Continuous Light (instead of traditional flash)</span>
                </label>
              </div>
            </div>

            <div>
              <Label>Address</Label>
              <Input
                value={spotForm.address || ''}
                onChange={(e) => setSpotForm({...spotForm, address: e.target.value})}
              />
            </div>

            {/* Assigned Photographers Section */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold mb-3 block">Assigned Photographers</Label>
              
              {/* Current Assignments */}
              <div className="space-y-2 mb-3">
                {(spotForm.assigned_photographers || []).map(photogId => {
                  const photog = availablePhotographers.find(p => p.id === photogId);
                  return photog ? (
                    <div key={photogId} className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div>
                        <p className="font-medium text-sm">{photog.name}</p>
                        <p className="text-xs text-gray-600">{photog.email}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRemovePhotographer(photogId)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : null;
                })}
                {(!spotForm.assigned_photographers || spotForm.assigned_photographers.length === 0) && (
                  <p className="text-sm text-gray-500 italic">No photographers assigned</p>
                )}
              </div>

              {/* Add Photographer */}
              <div>
                <select
                  className="w-full border rounded-lg p-2 text-sm"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddPhotographer(e.target.value);
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">+ Add Photographer</option>
                  {availablePhotographers
                    .filter(p => !(spotForm.assigned_photographers || []).includes(p.id))
                    .map(photog => (
                      <option key={photog.id} value={photog.id}>
                        {photog.name} - {photog.email}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {/* Reference Images Section */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold mb-3 block">🖼️ Reference Images</Label>
              
              {/* Existing Images */}
              {spotForm.reference_images && spotForm.reference_images.length > 0 && (
                <div className="grid grid-cols-3 gap-3 mb-3">
                  {spotForm.reference_images.map((imgUrl, idx) => {
                    // Handle both full URLs and relative paths
                    const imageSource = imgUrl.startsWith('http') ? imgUrl : `${BACKEND_URL}${imgUrl}`;
                    
                    return (
                    <div key={`${imgUrl}-${idx}`} className="relative group border rounded overflow-hidden bg-gray-100">
                      <img 
                        src={imageSource} 
                        alt={`Reference ${idx + 1}`}
                        className="w-full h-32 object-cover"
                        onError={(e) => {
                          console.error('Failed to load image:', imageSource);
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage Error%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                        <button
                          onClick={() => {
                            const fullUrl = `${window.location.origin}${BACKEND_URL}${imgUrl}`;
                            navigator.clipboard.writeText(fullUrl);
                            toast.success('📋 Image URL copied!');
                          }}
                          className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
                        >
                          Copy URL
                        </button>
                        <button
                          onClick={async () => {
                            if (confirm('Delete this image?')) {
                              try {
                                // Refetch the spot to get current state before deletion
                                const spotResponse = await axios.get(
                                  `${API}/spots/${editingSpot.id}`,
                                  config
                                );
                                const currentSpot = spotResponse.data;
                                
                                // Find the current index of this image URL
                                const currentIndex = currentSpot.reference_images.findIndex(url => url === imgUrl);
                                
                                if (currentIndex === -1) {
                                  toast.error('Image not found');
                                  return;
                                }
                                
                                // Delete using the current index
                                await axios.delete(
                                  `${API}/spots/${editingSpot.id}/images/${currentIndex}`,
                                  config
                                );
                                
                                // Refetch the spot data to get updated reference_images array
                                const updatedSpotResponse = await axios.get(
                                  `${API}/spots/${editingSpot.id}`,
                                  config
                                );
                                const updatedImages = updatedSpotResponse.data.reference_images || [];
                                setSpotForm({...spotForm, reference_images: updatedImages});
                                toast.success('Image removed from spot');
                              } catch (error) {
                                console.error('Error deleting image:', error);
                                toast.error('Failed to delete image');
                              }
                            }
                          }}
                          className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )})}
                </div>
              )}
              
              {/* Upload New Image */}
              <div className="border-2 border-dashed border-gray-300 rounded p-4 text-center hover:border-gray-400 transition-colors mb-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        const response = await axios.post(
                          `${API}/spots/${editingSpot.id}/upload-image`,
                          formData,
                          { 
                            headers: { 
                              Authorization: `Bearer ${localStorage.getItem('token')}`,
                              'Content-Type': 'multipart/form-data'
                            } 
                          }
                        );
                        
                        const newImages = [...(spotForm.reference_images || []), response.data.image_url];
                        setSpotForm({...spotForm, reference_images: newImages});
                        toast.success('📸 Image uploaded successfully!');
                        e.target.value = ''; // Reset input
                      } catch (error) {
                        console.error('Error uploading image:', error);
                        toast.error('Failed to upload image');
                      }
                    }
                  }}
                  className="hidden"
                  id="spot-image-upload-plan"
                />
                <label 
                  htmlFor="spot-image-upload-plan" 
                  className="cursor-pointer text-sm text-gray-600 hover:text-gray-800"
                >
                  <div className="flex flex-col items-center">
                    <Upload className="w-8 h-8 mb-2 text-gray-400" />
                    <span className="font-medium">Click to upload image</span>
                    <span className="text-xs text-gray-500 mt-1">PNG, JPG, GIF, WebP up to 10MB</span>
                  </div>
                </label>
              </div>
              
              {/* Select from Library Button */}
              <div className="mt-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowLibrarySelector(true)}
                  className="w-full"
                >
                  <Image className="w-4 h-4 mr-2" />
                  Select from Image Library
                </Button>
              </div>
            </div>

            <div className="flex gap-4 pt-4 border-t">
              <Button onClick={handleSaveSpot} className="bg-emerald-600 hover:bg-emerald-700 flex-1">
                <Save className="w-4 h-4 mr-2" />
                Save All Changes
              </Button>
              <Button variant="outline" onClick={() => setEditSpotDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Library Selector Dialog */}
      <Dialog open={showLibrarySelector} onOpenChange={setShowLibrarySelector}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📚 Select Images from Library</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Search and Filter */}
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  placeholder="🔍 Search images..."
                  value={librarySearchTerm}
                  onChange={(e) => setLibrarySearchTerm(e.target.value)}
                />
              </div>
              <select
                value={selectedLibraryPack}
                onChange={(e) => setSelectedLibraryPack(e.target.value)}
                className="border rounded px-3 py-2"
              >
                <option value="All">All Packs</option>
                {/* Show packs that match selected modalities first */}
                {spotForm.modalities && spotForm.modalities.length > 0 && (
                  <optgroup label="Matching Modalities">
                    {libraryPacks
                      .filter(pack => spotForm.modalities.some(mod => 
                        pack.name.toLowerCase().includes(mod.toLowerCase()) || 
                        mod.toLowerCase().includes(pack.name.toLowerCase())
                      ))
                      .map(pack => (
                        <option key={pack.id} value={pack.name}>
                          {pack.name} ({pack.image_count || 0})
                        </option>
                      ))}
                  </optgroup>
                )}
                <optgroup label="All Packs">
                  {libraryPacks.map(pack => (
                    <option key={pack.id} value={pack.name}>
                      {pack.name} ({pack.image_count || 0})
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>

            {/* Selected Count */}
            {selectedLibraryImages.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded px-4 py-2 text-sm">
                ✅ {selectedLibraryImages.length} image(s) selected
              </div>
            )}

            {/* Images Grid */}
            <div className="grid grid-cols-4 gap-3">
              {libraryImages
                .filter(img => {
                  const matchesPack = selectedLibraryPack === 'All' || img.pack === selectedLibraryPack;
                  const matchesSearch = !librarySearchTerm || 
                    img.name?.toLowerCase().includes(librarySearchTerm.toLowerCase()) ||
                    img.pack?.toLowerCase().includes(librarySearchTerm.toLowerCase());
                  return matchesPack && matchesSearch;
                })
                .map(image => {
                  const isSelected = selectedLibraryImages.includes(image.id);
                  return (
                    <div
                      key={image.id}
                      onClick={() => toggleLibraryImageSelection(image.id)}
                      className={`relative cursor-pointer border-2 rounded overflow-hidden transition-all ${
                        isSelected ? 'border-blue-500 ring-2 ring-blue-300' : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <img
                        src={`${BACKEND_URL}${image.image_url}`}
                        alt={image.name}
                        className="w-full h-32 object-cover"
                      />
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
                          ✓
                        </div>
                      )}
                      <div className="p-2 bg-white">
                        <p className="text-xs font-medium truncate">{image.name}</p>
                        <p className="text-xs text-gray-500">{image.pack}</p>
                      </div>
                    </div>
                  );
                })}
            </div>

            {libraryImages.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <div className="mb-4">
                  <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-lg font-medium text-gray-700 mb-2">No Images in Library</p>
                <p className="text-sm mb-4">Upload images to the Image Library to use them here</p>
                <a 
                  href={`/events/${eventId}/image-library`}
                  target="_blank"
                  className="inline-block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Go to Image Library
                </a>
              </div>
            ) : libraryImages.filter(img => {
              const matchesPack = selectedLibraryPack === 'All' || img.pack === selectedLibraryPack;
              const matchesSearch = !librarySearchTerm || 
                img.name?.toLowerCase().includes(librarySearchTerm.toLowerCase()) ||
                img.pack?.toLowerCase().includes(librarySearchTerm.toLowerCase());
              return matchesPack && matchesSearch;
            }).length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="mb-2">No images match your filters</p>
                <button
                  onClick={() => {
                    setLibrarySearchTerm('');
                    setSelectedLibraryPack('All');
                  }}
                  className="text-blue-500 hover:underline text-sm"
                >
                  Clear filters
                </button>
              </div>
            ) : null}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowLibrarySelector(false);
                  setSelectedLibraryImages([]);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddLibraryImages}
                disabled={selectedLibraryImages.length === 0}
              >
                Add {selectedLibraryImages.length > 0 ? `(${selectedLibraryImages.length})` : ''} to Spot
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PlanShareDialog
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
        planId={planId}
      />
    </div>
  );
}

// Photographer Section Component
function PhotographerSection({
  section, 
  sectionIndex, 
  onDragStart, 
  onDragOver, 
  onDrop, 
  isDragging,
  getStaticMapUrl,
  getDirectionsUrl,
  onEditSpot
}) {
  // Generate consistent color for photographer based on their name/index
  const getPhotographerColor = (index) => {
    const colors = [
      { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-900', accent: '#3b82f6', gradient: 'from-blue-500 to-blue-600' },
      { bg: 'bg-emerald-100', border: 'border-emerald-400', text: 'text-emerald-900', accent: '#10b981', gradient: 'from-emerald-500 to-emerald-600' },
      { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-900', accent: '#a855f7', gradient: 'from-purple-500 to-purple-600' },
      { bg: 'bg-orange-100', border: 'border-orange-400', text: 'text-orange-900', accent: '#f97316', gradient: 'from-orange-500 to-orange-600' },
      { bg: 'bg-pink-100', border: 'border-pink-400', text: 'text-pink-900', accent: '#ec4899', gradient: 'from-pink-500 to-pink-600' },
      { bg: 'bg-cyan-100', border: 'border-cyan-400', text: 'text-cyan-900', accent: '#06b6d4', gradient: 'from-cyan-500 to-cyan-600' },
      { bg: 'bg-red-100', border: 'border-red-400', text: 'text-red-900', accent: '#ef4444', gradient: 'from-red-500 to-red-600' },
      { bg: 'bg-yellow-100', border: 'border-yellow-400', text: 'text-yellow-900', accent: '#eab308', gradient: 'from-yellow-500 to-yellow-600' },
    ];
    return colors[index % colors.length];
  };

  const color = getPhotographerColor(sectionIndex);
  const totalTime = calculateTotalTime(section.spots);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, sectionIndex)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, sectionIndex)}
      className={`bg-white rounded-xl shadow-lg border-2 ${color.border} overflow-hidden transition-all ${
        isDragging ? 'opacity-50 scale-95' : `hover:shadow-xl hover:border-${color.border.split('-')[1]}-500`
      }`}
    >
      {/* Photographer Header */}
      <div className={`bg-gradient-to-r ${color.gradient} p-6 cursor-move`}>
        <div className="flex items-center gap-4">
          <GripVertical className="w-6 h-6 text-white/70" />
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-white">{section.photographer_name}</h3>
            <p className="text-white/90 text-sm mt-1">
              {section.spots.length} spot{section.spots.length !== 1 ? 's' : ''} assigned
              {totalTime && <span className="ml-3">• {totalTime}</span>}
            </p>
          </div>
          <Users className="w-8 h-8 text-white/50" />
        </div>
      </div>

      {/* Spots List */}
      <div className="divide-y divide-gray-200">
        {section.spots.map((spot, spotIndex) => (
          <SpotCard
            key={spot.spot_id}
            spot={spot}
            spotIndex={spotIndex}
            getStaticMapUrl={getStaticMapUrl}
            getDirectionsUrl={getDirectionsUrl}
            onEditSpot={onEditSpot}
          />
        ))}
      </div>
    </div>
  );
}

// Spot Card Component
function SpotCard({ spot, spotIndex, getStaticMapUrl, getDirectionsUrl, onEditSpot }) {
  // Get first 3 reference images - use correct field name
  const imageRefs = spot.reference_images?.slice(0, 3) || [];
  const [mapError, setMapError] = React.useState(false);

  return (
    <div className="p-6 hover:bg-gray-50 transition">
      {/* Spot Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h4 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            {spot.is_coverage && (
              <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full font-medium" title="Break coverage">
                🔁 Coverage
              </span>
            )}
            {spot.spot_name}
          </h4>
          {spot.is_coverage && (
            <p className="text-xs text-amber-700 mt-1">
              Covering {spot.coverage_for_photographer_name ? <>for <b>{spot.coverage_for_photographer_name}</b></> : 'the break'}
              {spot.coverage_note && <> · {spot.coverage_note}</>}
            </p>
          )}
          {spot.modalities && spot.modalities.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {spot.modalities.map((mod, idx) => (
                <span
                  key={idx}
                  className="text-xs bg-blue-500 text-white px-3 py-1 rounded-full font-medium"
                >
                  {mod}
                </span>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-4">
          {(spot.arrive_time || spot.leave_time) && (
            <div className="text-right">
              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="w-4 h-4" />
                <span className="font-semibold">
                  {spot.arrive_time && spot.leave_time
                    ? `${spot.arrive_time} - ${spot.leave_time}`
                    : spot.arrive_time || spot.leave_time}
                </span>
              </div>
            </div>
          )}
          
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEditSpot(spot)}
            className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
          >
            Edit Spot
          </Button>
        </div>
      </div>

      {/* Description */}
      {spot.description && (
        <p className="text-gray-600 mb-4">{spot.description}</p>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-12 gap-6">
        {/* Map (3 columns) - Real Google Map */}
        {spot.lat && spot.lng && (
          <div className="col-span-3">
            <div 
              className="relative rounded-lg overflow-hidden border-2 border-emerald-200 shadow-md h-48"
              data-spot-map="true"
              data-spot-id={spot.id}
            >
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={{ lat: spot.lat, lng: spot.lng }}
                zoom={15}
                options={{
                  disableDefaultUI: true,
                  zoomControl: false,
                  mapTypeControl: false,
                  streetViewControl: false,
                  fullscreenControl: false,
                }}
              >
                <Marker position={{ lat: spot.lat, lng: spot.lng }} />
              </GoogleMap>
              
              {/* Directions Overlay - Only at bottom */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-3">
                <a
                  href={getDirectionsUrl(spot.lat, spot.lng)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 text-white text-sm font-semibold hover:text-emerald-300 transition"
                >
                  <Navigation className="w-5 h-5" />
                  Directions
                </a>
              </div>
            </div>
            {spot.address && (
              <p className="text-xs text-gray-600 mt-2 line-clamp-3">
                <MapPin className="w-3 h-3 inline mr-1" />
                {spot.address}
              </p>
            )}
          </div>
        )}

        {/* Reference Images - Horizontal layout with aspect ratio preserved */}
        <div className={`${spot.lat && spot.lng ? 'col-span-9' : 'col-span-12'}`}>
          {imageRefs.length > 0 ? (
            <div>
              <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Reference Images ({imageRefs.length})
              </h5>
              <div className="flex gap-4 overflow-x-auto pb-2">
                {imageRefs.map((imgRef, idx) => (
                  <div key={idx} className="flex-shrink-0 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-emerald-400 transition bg-white shadow-sm">
                    <img
                      src={`${BACKEND_URL}${imgRef}`}
                      alt={`Reference ${idx + 1}`}
                      className="h-64 w-auto object-contain"
                      style={{ maxWidth: '400px' }}
                      onError={(e) => {
                        console.error('Failed to load image:', imgRef);
                        e.target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='256'%3E%3Crect fill='%23f3f4f6' width='300' height='256'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='14' fill='%239ca3af'%3EImage ${idx + 1}%3C/text%3E%3C/svg%3E`;
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 min-h-[200px]">
              <div className="text-center text-gray-400">
                <Image className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">No reference images</p>
              </div>
            </div>
          )}
        </div>

        {/* Gear and Details (3 columns) */}
        <div className="col-span-3 space-y-3 text-sm">
          {spot.required_gear && spot.required_gear.length > 0 && (
            <div>
              <span className="font-semibold text-gray-700 flex items-center gap-1 mb-1">
                <Camera className="w-4 h-4" />
                Gear Required:
              </span>
              <div className="space-y-1">
                {spot.required_gear.map((gear, idx) => (
                  <div key={idx} className="text-xs bg-emerald-50 border border-emerald-200 px-2 py-1 rounded text-emerald-700">
                    • {gear}
                  </div>
                ))}
              </div>
            </div>
          )}
          {spot.first_athlete_time && (
            <div>
              <span className="font-semibold text-gray-700">First Athlete:</span>
              <span className="ml-2 text-gray-600">{spot.first_athlete_time}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Global CSS for rich text content */}
      <style jsx>{`
        .prose p {
          margin-bottom: 1em;
          line-height: 1.6;
        }
        .prose p:empty {
          height: 1em;
          margin: 0.5em 0;
        }
        .prose br {
          display: block;
          content: "";
          margin-top: 0.5em;
        }
        .prose h1, .prose h2, .prose h3 {
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          line-height: 1.3;
        }
        .prose h1 {
          font-size: 2em;
          font-weight: bold;
        }
        .prose h2 {
          font-size: 1.5em;
          font-weight: bold;
        }
        .prose h3 {
          font-size: 1.25em;
          font-weight: bold;
        }
        .prose ul, .prose ol {
          margin: 1em 0;
          padding-left: 2em;
        }
        .prose li {
          margin: 0.5em 0;
        }
        .prose img {
          max-width: 100%;
          height: auto;
          margin: 1em 0;
          border-radius: 0.5rem;
        }
        .prose iframe {
          max-width: 100%;
          margin: 1em 0;
          border-radius: 0.5rem;
        }
        .prose strong {
          font-weight: 600;
        }
        .prose em {
          font-style: italic;
        }
        .prose table {
          width: 100%;
          border-collapse: collapse;
          margin: 1em 0;
        }
        .prose table td, .prose table th {
          border: 1px solid #ddd;
          padding: 0.5em;
        }
        .prose table th {
          background-color: #f3f4f6;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
}

// Helper function to calculate total time for a photographer
function calculateTotalTime(spots) {
  if (spots.length === 0) return null;
  
  const times = spots
    .filter(s => s.arrive_time && s.leave_time)
    .map(s => ({ arrive: s.arrive_time, leave: s.leave_time }));
  
  if (times.length === 0) return null;
  
  const earliest = times.reduce((min, t) => t.arrive < min ? t.arrive : min, times[0].arrive);
  const latest = times.reduce((max, t) => t.leave > max ? t.leave : max, times[0].leave);
  
  return `${earliest} - ${latest}`;
}
